<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  AdminEditorTopBar,
  type AdminEditorPreviewViewport,
  UiChip,
  UiDialog,
  UiIconButton,
  UiInfoBox,
  UiStandardButton,
  UiText,
  UiTooltip,
  useApi,
  useToaster,
} from '@shopexperts/shared';
import type { AdminPage } from '@shopexperts/shared';
import { useSchema } from '../composables/useSchema';
import { usePageSchemas } from '../composables/usePageSchemas';
import { useSchemaPages } from '../composables/useSchemaPages';
import { useUpdatePage } from '../composables/useUpdatePage';
import { useCreatePage } from '../composables/useCreatePage';
import { useDeletePage } from '../composables/useDeletePage';
import { useCreateRedirect } from '../composables/useCreateRedirect';
import { getAdminPagesEditorConfig } from '../config';
import PageMetafieldsForm from '../components/page-metafields-editor/PageMetafieldsForm.vue';
import PageEditorPreviewPanel from '../components/page-metafields-editor/PageEditorPreviewPanel.vue';
import PageEditorEmptyState from '../components/page-metafields-editor/PageEditorEmptyState.vue';
import PageNavigatorPanel from '../components/page-metafields-editor/PageNavigatorPanel.vue';
import PagePathRedirectNotice from '../components/page-metafields-editor/PagePathRedirectNotice.vue';
import { usePageMetafieldsPreview } from '../composables/usePageMetafieldsPreview';
import { usePreviewMetafieldEntities } from '../composables/usePreviewMetafieldEntities';
import { usePreviewTargetPreference } from '../composables/usePreviewTargetPreference';
import { classifyPreviewPathChange } from '../utils/previewPathSync';
import { buildPathChangeRedirect, shouldOfferRedirect, type PageEditorMode } from '../utils/pathChangeRedirect';
import { normalizeSuperCollectionPreset, validateSuperCollectionPath } from '../utils/superCollectionPreset';
import { buildMetafields, buildPreviewMetafields, initializeMetafields } from '../utils/pageMetafieldsDraft';
import {
  buildPageMetafieldsPreviewUrl,
  getPreviewTargetOptions,
  normalizePagePath as normalizePagePathForSite,
  resolvePreviewSiteUrl,
} from '../utils/metafieldsPreviewUrl';

const route = useRoute();
const router = useRouter();
const api = useApi();
const toaster = useToaster();
const config = getAdminPagesEditorConfig();

const schemaId = computed(() => {
  const id = route.params.schemaId;
  return typeof id === 'string' ? parseInt(id, 10) : 0;
});

const routePageId = computed(() => {
  const id = route.params.pageId;
  return typeof id === 'string' ? parseInt(id, 10) : null;
});

const pagesPanelOpen = ref(true);
const pageSearch = ref('');
const selectedPage = ref<AdminPage | null>(null);
/**
 * 'empty' is the landing state with nothing selected, 'new' an explicit draft,
 * 'edit' a stored page. Only 'empty' hides the form and preview, so an unsaved
 * draft never looks like a page that failed to load.
 */
const mode = ref<PageEditorMode>('empty');
/** Schema the current draft belongs to, so switching schemas discards it. */
const draftSchemaId = ref<number | null>(null);
const savedPath = ref<string | null>(null);
const shouldCreateRedirect = ref(true);
const deleteConfirmOpen = ref(false);
const isPreviewPathSyncPaused = ref(false);
const pendingPreviewPath = ref<string | null>(null);
const pathDraft = ref('');
const statusDraft = ref<'draft' | 'published'>('draft');
const metafieldsDraft = ref<Record<string, any>>({});
const iframeRef = ref<HTMLIFrameElement | null>(null);
const previewPanelRef = ref<InstanceType<typeof PageEditorPreviewPanel> | null>(null);
const viewport = ref<AdminEditorPreviewViewport>('fit');
const isPreviewFullscreen = ref(false);
const previewError = ref('');
const previewCacheBuster = ref(Date.now());
const previewTarget = usePreviewTargetPreference();
const previewTargetOptions = getPreviewTargetOptions(config);
if (!previewTargetOptions.some(option => option.value === previewTarget.value)) {
  previewTarget.value = 'live';
}
const previewSiteUrl = computed(() => resolvePreviewSiteUrl(previewTarget.value, config));
const previewOrigin = computed(() => {
  try {
    return new URL(previewSiteUrl.value).origin;
  } catch {
    return previewSiteUrl.value;
  }
});
const isLoadingRoutePage = ref(Boolean(routePageId.value));
const routePageError = ref<Error | null>(null);
let routePageRequestRevision = 0;
let pageLookupRevision = 0;

const { schema, isLoading: isLoadingSchema, error: schemaError } = useSchema(schemaId);
const { schemas, isLoading: isLoadingSchemas } = usePageSchemas();
const { pages, isLoading: isLoadingPages, error: pagesError, hasNextPage, isFetchingNextPage, loadMore } = useSchemaPages(schemaId, 50, pageSearch);
const { mutateAsync: updatePage, isPending: isUpdating } = useUpdatePage();
const { mutateAsync: createPage, isPending: isCreating } = useCreatePage();
const { mutateAsync: deletePage, isPending: isDeleting } = useDeletePage();
const { mutateAsync: createRedirect } = useCreateRedirect();

const isSaving = computed(() => isUpdating.value || isCreating.value);
const error = computed(() => schemaError.value || pagesError.value || routePageError.value);
/**
 * The page the preview is actually showing. A stored page keeps rendering at
 * its saved path while its path is being edited: nothing is served at the new
 * URL until the rename is saved, and the website only applies the live
 * metafields when the payload path matches the page it rendered — so following
 * the draft would both 404 the frame and stop real-time updates.
 */
const previewPath = computed(() => {
  if (mode.value === 'edit' && selectedPage.value) return normalizePagePath(selectedPage.value.path);
  const draft = pathDraft.value.trim();
  return draft ? normalizePagePath(draft) : '';
});
const previewUrl = computed(() => buildPageMetafieldsPreviewUrl({
  path: previewPath.value || '/',
  siteUrl: previewSiteUrl.value,
  cacheBust: previewCacheBuster.value,
  adminPreview: true,
}));
const superCollectionPresetField = computed(() => schema.value?.fields.find(field => field.key === 'preset_filters_query'));
const isSuperCollectionSchema = computed(() => Boolean(superCollectionPresetField.value));
const superCollectionPresetValidation = computed(() => (
  isSuperCollectionSchema.value
    ? normalizeSuperCollectionPreset(metafieldsDraft.value.preset_filters_query)
    : { normalized: '', errors: [] }
));
const pagePathError = computed(() => (
  isSuperCollectionSchema.value ? validateSuperCollectionPath(pathDraft.value) : ''
));
const canSavePage = computed(() => Boolean(
  schema.value
  && mode.value !== 'empty'
  && pathDraft.value.trim()
  && !pagePathError.value
  && !superCollectionPresetValidation.value.errors.length
));
const normalizedDraftPath = computed(() => (
  pathDraft.value.trim() ? normalizePagePath(pathDraft.value) : ''
));
const pathChangeState = computed(() => ({
  mode: mode.value,
  savedPath: savedPath.value,
  draftPath: normalizedDraftPath.value,
}));
const showRedirectNotice = computed(() => shouldOfferRedirect(pathChangeState.value));
const { entities: previewEntities } = usePreviewMetafieldEntities(schema, metafieldsDraft);
const previewPayload = computed(() => ({
  path: previewPath.value || '/',
  page: selectedPage.value,
  metafields: buildPreviewMetafields(schema.value, metafieldsDraft.value, {
    source: selectedPage.value?.metafields || {},
    entities: previewEntities.value,
  }),
}));
const viewportOptions = [
  { label: 'Fit', value: 'fit' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Tablet', value: 'tablet' },
  { label: 'Desktop', value: 'desktop' },
] as const;
const viewportWidth = computed(() => {
  if (viewport.value === 'mobile') return 390;
  if (viewport.value === 'tablet') return 768;
  if (viewport.value === 'desktop') return 1440;
  return null;
});
const statusLabel = computed(() => {
  if (isSaving.value) return 'Saving';
  if (mode.value === 'empty') return 'No page selected';
  if (!selectedPage.value) return 'New page';
  return statusDraft.value === 'published' ? 'Published' : 'Draft';
});
const isPublished = computed(() => statusDraft.value === 'published');
const statusChipVariant = computed(() => (
  selectedPage.value && isPublished.value ? 'success' : 'neutral'
));
const publishActionLabel = computed(() => (isPublished.value ? 'Unpublish' : 'Publish'));
const { previewRevision, syncFrame } = usePageMetafieldsPreview(
  iframeRef,
  previewPayload,
  handlePreviewPathChange,
  previewOrigin,
);

watch(previewTarget, () => {
  detachPreviewFrame();
  expectPreviewPath(previewPath.value || null);
});

watch([schemaId, routePageId, schema, isLoadingSchema], async () => {
  const requestedPageId = routePageId.value;
  const requestRevision = ++routePageRequestRevision;
  routePageError.value = null;

  // Starting a draft drops the :pageId from the route; that must not be read
  // back as "nothing selected" and wipe the draft the user is filling in.
  if (!requestedPageId && mode.value === 'new' && draftSchemaId.value === schemaId.value) {
    isLoadingRoutePage.value = false;
    return;
  }

  if (!requestedPageId) {
    selectPage(null, false);
    isLoadingRoutePage.value = false;
    return;
  }
  if (!schema.value) {
    if (!isLoadingSchema.value) isLoadingRoutePage.value = false;
    return;
  }
  if (selectedPage.value?.id === requestedPageId) {
    isLoadingRoutePage.value = false;
    return;
  }

  isLoadingRoutePage.value = true;
  try {
    const localPage = pages.value.find((page) => page.id === requestedPageId);
    const page = localPage || await api.admin.pages.getPage({ params: { id: requestedPageId } });
    if (requestRevision !== routePageRequestRevision || routePageId.value !== requestedPageId) return;
    if (page.schema?.id !== schemaId.value) throw new Error('The selected page does not belong to this schema');
    selectPage(page, false);
  } catch (error) {
    if (requestRevision !== routePageRequestRevision) return;
    routePageError.value = error instanceof Error ? error : new Error('Failed to load the selected page');
  } finally {
    if (requestRevision === routePageRequestRevision) isLoadingRoutePage.value = false;
  }
}, { immediate: true });

watch(schema, () => {
  // Only reseed the blank state; a draft in progress keeps what was typed.
  if (mode.value === 'empty' && schema.value) {
    metafieldsDraft.value = initializeMetafields(schema.value, {});
  }
});

// Keyed on the previewed page, not the draft path, so editing the path of a
// stored page never reloads the frame out from under a live edit.
watch(previewPath, (value, previousValue) => {
  if (!value) {
    expectPreviewPath(null);
    return;
  }
  if (!isPreviewPathSyncPaused.value && pendingPreviewPath.value === value) return;
  if (value !== previousValue) detachPreviewFrame();
  expectPreviewPath(value);
});

watch(previewPanelRef, (panel) => {
  const frame = panel?.getFrame();
  if (frame) iframeRef.value = frame;
}, { flush: 'post' });

function goBack() {
  router.push({ name: 'admin-pages' });
}

function openSchemaBuilder() {
  router.push({ name: 'admin-page-schema-builder', params: { schemaId: schemaId.value } });
}

function selectSchema(nextSchemaId: number) {
  if (nextSchemaId === schemaId.value) return;
  pageSearch.value = '';
  router.push({ name: 'admin-page-metafields-editor', params: { schemaId: nextSchemaId } });
}

function selectPage(page: AdminPage | null, updateRoute = true) {
  previewError.value = '';
  const currentPreviewIdentity = `${previewPath.value}:${selectedPage.value?.id ?? 'new'}`;
  const nextPreviewIdentity = `${normalizePagePath(page?.path || '/')}:${page?.id ?? 'new'}`;
  if (currentPreviewIdentity !== nextPreviewIdentity) detachPreviewFrame();
  mode.value = page ? 'edit' : 'empty';
  draftSchemaId.value = null;
  savedPath.value = page?.path || null;
  shouldCreateRedirect.value = true;
  expectPreviewPath(page?.path || null);
  selectedPage.value = page;
  pathDraft.value = page?.path || '';
  statusDraft.value = page?.status || 'draft';
  metafieldsDraft.value = initializeMetafields(schema.value, page?.metafields || {});

  if (page && updateRoute) {
    router.replace({ name: 'admin-page-metafields-editor-page', params: { schemaId: schemaId.value, pageId: page.id } });
  }
}

function normalizePagePath(value: string): string {
  return normalizePagePathForSite(value, previewSiteUrl.value);
}

async function findPageByPath(path: string): Promise<AdminPage | null> {
  const localMatch = pages.value.find((page) => page.path === path);
  if (localMatch) return localMatch;
  const result = await api.admin.pages.listPages({
    query: {
      schema_id: schemaId.value,
      search: path,
      limit: 10,
    },
  });
  return result.data.find((page) => page.path === path) || null;
}

async function commitPreviewPath(value: string) {
  previewError.value = '';
  const nextPath = normalizePagePath(value);
  const lookupRevision = ++pageLookupRevision;
  const matchingPage = await findPageByPath(nextPath);
  if (lookupRevision !== pageLookupRevision) return;
  if (matchingPage && matchingPage.id !== selectedPage.value?.id) {
    selectPage(matchingPage);
    return;
  }
  // No page lives here yet, so this is a rename of the current page rather than
  // preview navigation. The previewPath watcher reloads the frame only if the
  // previewed page actually changed.
  pathDraft.value = nextPath;
}

async function handlePreviewPathChange(path: string) {
  const normalized = normalizePagePath(path);
  const decision = classifyPreviewPathChange({
    reportedPath: normalized,
    editorPath: previewPath.value,
    pendingPath: pendingPreviewPath.value,
    paused: isPreviewPathSyncPaused.value || !previewPath.value,
  });
  if (decision === 'acknowledge') {
    pendingPreviewPath.value = null;
    return;
  }
  if (decision === 'ignore') return;

  pendingPreviewPath.value = null;
  const lookupRevision = ++pageLookupRevision;
  const matchingPage = await findPageByPath(normalized);
  if (lookupRevision !== pageLookupRevision || isPreviewPathSyncPaused.value) return;
  if (normalized === previewPath.value) return;
  if (matchingPage) {
    selectPage(matchingPage);
    return;
  }
  startDraftPage(normalized);
}

/** Opens an unsaved page draft, optionally seeded with a path the preview navigated to. */
function startDraftPage(path = '') {
  detachPreviewFrame();
  mode.value = 'new';
  draftSchemaId.value = schemaId.value;
  savedPath.value = null;
  shouldCreateRedirect.value = true;
  expectPreviewPath(path || null);
  selectedPage.value = null;
  pathDraft.value = path;
  statusDraft.value = 'draft';
  metafieldsDraft.value = initializeMetafields(schema.value, {});
  router.replace({ name: 'admin-page-metafields-editor', params: { schemaId: schemaId.value } });
}

function handlePreviewFrameLoad() {
  const frame = previewPanelRef.value?.getFrame();
  if (frame) iframeRef.value = frame;
  syncFrame();
}

function detachPreviewFrame() {
  iframeRef.value = null;
  previewCacheBuster.value = Math.max(Date.now(), previewCacheBuster.value + 1);
}

function expectPreviewPath(path: string | null) {
  pageLookupRevision += 1;
  if (!path?.trim()) {
    pendingPreviewPath.value = null;
    isPreviewPathSyncPaused.value = true;
    return;
  }
  pendingPreviewPath.value = normalizePagePath(path);
  isPreviewPathSyncPaused.value = false;
}

async function savePage(options: { successTitle?: string } = {}): Promise<boolean> {
  if (mode.value === 'empty') {
    toaster.create({ title: 'Select a page to edit first', type: 'error' });
    return false;
  }

  if (!schema.value || !pathDraft.value.trim()) {
    toaster.create({ title: 'Page path is required', type: 'error' });
    return false;
  }

  if (pagePathError.value || superCollectionPresetValidation.value.errors.length) {
    toaster.create({
      title: pagePathError.value || superCollectionPresetValidation.value.errors[0] || 'Invalid super-collection settings',
      type: 'error',
    });
    return false;
  }

  const normalizedPath = normalizePagePath(pathDraft.value);
  const pendingRedirect = shouldCreateRedirect.value
    ? buildPathChangeRedirect({ ...pathChangeState.value, draftPath: normalizedPath })
    : null;

  try {
    if (selectedPage.value) {
      const updated = await updatePage({
        id: selectedPage.value.id,
        schemaId: schema.value.id,
        data: {
          path: normalizedPath,
          status: statusDraft.value,
          metafields: buildMetafields(schema.value, metafieldsDraft.value),
        },
      });
      selectPage(updated);
      toaster.create({ title: options.successTitle || 'Page saved', type: 'success' });
      // The page already moved; a failed redirect is reported by useCreateRedirect
      // and must not read as a failed save.
      if (pendingRedirect) {
        try {
          await createRedirect(pendingRedirect);
        } catch {
          // Reported by the mutation's own error toast.
        }
      }
      return true;
    }

    const created = await createPage({
      schema_id: schema.value.id,
      path: normalizedPath,
      status: statusDraft.value,
      metafields: buildMetafields(schema.value, metafieldsDraft.value),
    });
    selectPage(created);
    toaster.create({ title: options.successTitle || 'Page created', type: 'success' });
    return true;
  } catch {
    toaster.create({ title: 'Failed to save page', type: 'error' });
    return false;
  }
}

/**
 * Publishing saves the page with the new status, so the live page always
 * matches what the editor is showing. The draft status is restored when the
 * save fails, keeping the badge honest.
 */
async function togglePublishStatus() {
  const previousStatus = statusDraft.value;
  const nextStatus = previousStatus === 'published' ? 'draft' : 'published';
  statusDraft.value = nextStatus;

  const saved = await savePage({
    successTitle: nextStatus === 'published' ? 'Page published' : 'Page unpublished',
  });

  if (!saved) statusDraft.value = previousStatus;
}

async function confirmDeletePage() {
  const page = selectedPage.value;
  if (!page) return;

  try {
    await deletePage({ id: page.id, schemaId: schemaId.value });
    deleteConfirmOpen.value = false;
    selectPage(null, false);
    router.replace({ name: 'admin-page-metafields-editor', params: { schemaId: schemaId.value } });
  } catch {
    // Reported by the mutation's own error toast; keep the dialog open to retry.
  }
}
</script>

<template>
  <main class="page-metafields-editor flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
    <AdminEditorTopBar
      :sidebar-open="pagesPanelOpen"
      title="Page Metafields"
      :subtitle="schema?.label || 'Loading schema...'"
      :url-value="pathDraft"
      url-placeholder="/services/shopify-development-experts"
      :url-disabled="mode === 'empty'"
      :status-label="statusLabel"
      :can-save="canSavePage"
      :is-saving="isSaving"
      save-label="Save page"
      back-label="Back to schemas"
      sidebar-label-open="Close pages"
      sidebar-label-closed="Open pages"
      @back="goBack"
      @toggle-sidebar="pagesPanelOpen = !pagesPanelOpen"
      @submit-url="commitPreviewPath"
      @save="savePage()"
    >
      <template #status>
        <UiChip :text="statusLabel" :variant="statusChipVariant" size="sm" />
        <UiStandardButton
          :label="publishActionLabel"
          variant="outline"
          size="sm"
          :disabled="!canSavePage || isSaving"
          data-testid="page-publish-toggle"
          @click="togglePublishStatus"
        />
      </template>
    </AdminEditorTopBar>

    <div v-if="isLoadingSchema || isLoadingRoutePage" class="flex flex-1 items-center justify-center">
      <UiText as="p" variant="p" text="Loading editor..." />
    </div>

    <div v-else-if="error" class="flex flex-1 items-center justify-center p-8">
      <UiInfoBox variant="error">
        <UiText as="p" variant="p-small" :text="`Failed to load editor: ${error.message}`" />
      </UiInfoBox>
    </div>

    <div v-else-if="schema" class="flex min-h-0 flex-1 overflow-hidden">
      <PageNavigatorPanel
        v-if="pagesPanelOpen"
        v-model:search="pageSearch"
        :schema="schema"
        :schemas="schemas"
        :is-loading-schemas="isLoadingSchemas"
        :pages="pages"
        :selected-page-id="selectedPage?.id"
        :is-loading="isLoadingPages"
        :has-next-page="hasNextPage"
        :is-fetching-next-page="isFetchingNextPage"
        @new-page="startDraftPage()"
        @select-page="selectPage"
        @select-schema="selectSchema"
        @edit-schema="openSchemaBuilder"
        @load-more="loadMore"
      />

      <PageEditorEmptyState
        v-if="mode === 'empty'"
        :schema-label="schema.label"
        :has-pages="pages.length > 0"
        @new-page="startDraftPage()"
      />

      <section v-else class="page-metafields-editor__content min-w-0 flex-1 overflow-hidden">
        <div class="flex min-h-0 overflow-hidden border-r border-se-gray-200">
          <PageMetafieldsForm
            v-model="metafieldsDraft"
            v-model:path="pathDraft"
            :schema="schema"
            :path-error="pagePathError"
          >
            <template #path-notice>
              <PagePathRedirectNotice
                v-if="showRedirectNotice"
                v-model="shouldCreateRedirect"
                :from-path="savedPath || ''"
                :to-path="normalizedDraftPath"
              />
            </template>
            <template #actions>
              <UiTooltip content="New page">
                <UiIconButton
                  icon="plus"
                  aria-label="New page"
                  variant="ghost"
                  class="h-9 w-9"
                  data-testid="page-create"
                  @click="startDraftPage()"
                />
              </UiTooltip>
              <UiTooltip v-if="mode === 'edit'" content="Delete page">
                <UiIconButton
                  icon="delete"
                  aria-label="Delete page"
                  variant="ghost"
                  class="h-9 w-9 hover:text-red-600"
                  data-testid="page-delete"
                  @click="deleteConfirmOpen = true"
                />
              </UiTooltip>
            </template>
          </PageMetafieldsForm>
        </div>

        <PageEditorPreviewPanel
          ref="previewPanelRef"
          :url="previewUrl"
          :revision="previewRevision"
          :error="previewError"
          :label="selectedPage ? `Page #${selectedPage.id}` : 'New page'"
          :label-path="pathDraft"
          :is-fullscreen="isPreviewFullscreen"
          :viewport="viewport"
          :viewport-options="viewportOptions"
          :viewport-width="viewportWidth"
          :preview-target="previewTarget"
          :preview-target-options="previewTargetOptions"
          @update:preview-target="previewTarget = $event"
          @update:viewport="viewport = $event"
          @toggle-fullscreen="isPreviewFullscreen = !isPreviewFullscreen"
          @frame-ready="iframeRef = $event"
          @frame-load="handlePreviewFrameLoad"
        />
      </section>
    </div>

    <UiDialog
      v-model="deleteConfirmOpen"
      title="Delete page"
      :text="`Are you sure you want to delete '${selectedPage?.path}'? This action cannot be undone.`"
      primary-action-label="Delete"
      secondary-action-label="Cancel"
      :primary-action-loading="isDeleting"
      @primary-action="confirmDeletePage"
      @secondary-action="deleteConfirmOpen = false"
    />
  </main>
</template>

<style scoped>
.page-metafields-editor__content {
  display: grid;
  grid-template-columns: minmax(360px, 0.9fr) minmax(420px, 1.1fr);
  height: 100%;
  min-height: 0;
}

.page-metafields-editor {
  height: calc(100vh - 72px);
}

@media (max-width: 1100px) {
  .page-metafields-editor__content {
    grid-template-columns: minmax(320px, 1fr);
  }
}
</style>
